import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';

function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      const urlLanguage = searchParams.get('lang');
      
      // Use URL language if provided, otherwise use current language
      const currentLanguage = urlLanguage && ['pl', 'en', 'fr'].includes(urlLanguage) ? urlLanguage : language;
      
      // Set language from URL parameter if provided
      if (urlLanguage && ['pl', 'en', 'fr'].includes(urlLanguage)) {
        setLanguage(urlLanguage);
      }
      
      if (!token) {
        setStatus('error');
        setMessage(getTranslation('invalid-verification-link', currentLanguage));
        return;
      }

      // Prevent multiple verification attempts
      if (hasVerified.current) {
        return;
      }
      hasVerified.current = true;

      try {
        const response = await fetch(`/api/verify-email?token=${encodeURIComponent(token)}&lang=${currentLanguage}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          setStatus('success');
          const data = await response.json();
          setMessage(data.message || getTranslation('email-verified-success', currentLanguage));
        } else {
          const data = await response.json();
          // Check if the error is because the email is already verified or token is invalid (already used)
          if (data.detail && (
            data.detail.includes('already verified') || 
            data.detail.includes('Email already verified') ||
            data.detail.includes('Invalid verification token') ||
            data.detail.includes('już zweryfikowany') ||
            data.detail.includes('déjà vérifié')
          )) {
            setStatus('success');
            setMessage(getTranslation('email-already-verified-success', currentLanguage));
          } else {
            setStatus('error');
            setMessage(data.detail || getTranslation('verification-failed', currentLanguage));
          }
        }
      } catch (error) {
        setStatus('error');
        setMessage(getTranslation('connection-error', currentLanguage));
      }
    };

    verifyEmail();
  }, [searchParams, language]);

  const handleGoToLogin = () => {
    // Redirect to the root URL instead of relative path
    window.location.href = '/';
  };

  const handleResendEmail = () => {
    // This could be implemented later to resend verification emails
    const urlLanguage = searchParams.get('lang');
    const currentLanguage = urlLanguage && ['pl', 'en', 'fr'].includes(urlLanguage) ? urlLanguage : language;
    setMessage(getTranslation('contact-support-resend', currentLanguage));
  };

  // Get current language for UI elements
  const urlLanguage = searchParams.get('lang');
  const currentLanguage = urlLanguage && ['pl', 'en', 'fr'].includes(urlLanguage) ? urlLanguage : language;

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f0f8ff',
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: window.innerWidth <= 600 ? '20px 20px' : '40px 0',
      fontFamily: 'Nunito, Arial, sans-serif'
    }}>
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        padding: window.innerWidth <= 600 ? '0 20px' : '0'
      }}>
        ShootMe
      </h1>
      
      {/* Subtitle */}
      <p style={{
        fontSize: window.innerWidth <= 600 ? '1rem' : '1.2rem',
        color: '#6b7280',
        margin: '0 0 48px 0',
        textAlign: 'center',
        maxWidth: window.innerWidth <= 600 ? '90%' : '600px',
        lineHeight: '1.6',
        padding: window.innerWidth <= 600 ? '0 20px' : '0'
      }}>
        {getTranslation('email-verification', currentLanguage)}
      </p>

      <div style={{
        background: 'white',
        borderRadius: window.innerWidth <= 600 ? 16 : 20,
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
        maxWidth: window.innerWidth <= 600 ? '95%' : 500,
        width: window.innerWidth <= 600 ? '95%' : 500,
        padding: window.innerWidth <= 600 ? 24 : 32,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}>
        {status === 'verifying' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #7c3aed',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <h2 style={{ 
              color: '#7c3aed', 
              marginBottom: 16, 
              fontFamily: 'Nunito, Arial, sans-serif', 
              fontWeight: 700 
            }}>
              {getTranslation('verifying-email', currentLanguage)}
            </h2>
            <p style={{ color: '#6b7280', margin: 0 }}>
              {getTranslation('please-wait-verification', currentLanguage)}
            </p>
          </div>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '30px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              color: '#10b981', 
              marginBottom: 16, 
              fontFamily: 'Nunito, Arial, sans-serif', 
              fontWeight: 700 
            }}>
              {getTranslation('email-verified', currentLanguage)}
            </h2>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
              {message}
            </p>
            <button 
              onClick={handleGoToLogin}
              style={{
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#7c3aed',
                color: 'white',
                fontWeight: 700,
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {getTranslation('przejdz-do-strony-glownej', currentLanguage)}
            </button>
          </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: '#ef4444',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '30px',
              color: 'white'
            }}>
              ✕
            </div>
            <h2 style={{ 
              color: '#ef4444', 
              marginBottom: 16, 
              fontFamily: 'Nunito, Arial, sans-serif', 
              fontWeight: 700 
            }}>
              {getTranslation('verification-failed-title', currentLanguage)}
            </h2>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
              {message}
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button 
                onClick={handleGoToLogin}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#7c3aed',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {getTranslation('przejdz-do-strony-glownej', currentLanguage)}
              </button>
              <button 
                onClick={handleResendEmail}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: '1px solid #7c3aed',
                  background: 'transparent',
                  color: '#7c3aed',
                  fontWeight: 700,
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {getTranslation('resend-email', currentLanguage)}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default EmailVerification;

